import React, { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

interface Props {
  label: string;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  successLabel?: string;
  onConfirm: () => void | Promise<void>;
  className?: string;
}

export const TapToConfirm: React.FC<Props> = ({
  label,
  title = 'Confirmar ação?',
  description = 'Toque em confirmar para continuar.',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  successLabel,
  onConfirm,
  className = '',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);
  const [isConfirming, setIsConfirming] = React.useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isConfirming) return;

    setIsConfirming(true);
    try {
      await onConfirm();
      setIsOpen(false);
      setCompleted(true);
    } finally {
      setIsConfirming(false);
    }
  };

  if (completed && successLabel) {
    return (
      <div className={`h-16 w-full bg-sal-sage rounded-full flex items-center justify-center text-white font-black animate-pulse shadow-lg uppercase tracking-widest text-base ${className}`}>
        <CheckCircle2 className="w-6 h-6 mr-2" /> {successLabel}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(true);
        }}
        className={`h-16 w-full bg-sal-cyber-teal text-badge-text-inv rounded-full flex items-center justify-center gap-3 px-6 font-black uppercase tracking-[0.16em] text-sm shadow-xl active:scale-[0.98] transition-transform ${className}`}
      >
        <CheckCircle2 className="w-6 h-6" />
        <span>{label}</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="absolute inset-0" onClick={() => !isConfirming && setIsOpen(false)} />
          <div className="relative w-full max-w-sm bg-card-bg rounded-3xl p-6 shadow-2xl border border-border-dim animate-in fade-in zoom-in-95 duration-150" onClick={(event) => event.stopPropagation()}>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-status-warning/10 border border-status-warning/30 flex items-center justify-center text-status-warning">
                <AlertTriangle className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-text-primary uppercase tracking-tight">{title}</h3>
                <p className="text-sm text-text-muted font-bold mt-2 leading-relaxed">{description}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isConfirming}
                className="w-full py-4 bg-status-ready text-badge-text rounded-xl font-black text-base uppercase tracking-wider shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
              >
                <CheckCircle2 className="w-5 h-5" /> {isConfirming ? 'Confirmando...' : confirmLabel}
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!isConfirming) setIsOpen(false);
                }}
                disabled={isConfirming}
                className="w-full py-3 text-text-muted font-bold text-sm uppercase tracking-widest hover:text-text-primary transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
              >
                <X className="w-4 h-4" /> {cancelLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
